import { CSSProperties, ReactNode, useEffect, useRef, useState } from 'react';

/**
 * Tracks whether a scrollable element has more content below the fold.
 * Re-checks on scroll and on resize (list items expanding/collapsing
 * changes scrollHeight without a window resize).
 */
const useScrollOverflowHint = <T extends HTMLElement>() => {
  const ref = useRef<T>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const hasOverflow = el.scrollHeight > el.clientHeight + 1;
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 4;
      setVisible(hasOverflow && !atBottom);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    // A fixed-height scroll container's own box doesn't resize when its
    // content does (e.g. rows expanding via "expand all") — ResizeObserver
    // alone misses that. Watch the subtree directly so content-height
    // changes from expand/collapse still trigger a re-check.
    const mo = new MutationObserver(update);
    mo.observe(el, { childList: true, subtree: true });
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
      mo.disconnect();
    };
  }, []);

  return { ref, visible };
};

export interface ScrollHintListProps {
  /** Style applied to the scrollable div — pass maxHeight/overflowY/etc. */
  style?: CSSProperties;
  /** className applied to the scrollable div — for CSS-based scrollbar styling. */
  className?: string;
  children: ReactNode;
}

/**
 * Drop-in replacement for a `maxHeight` + `overflowY: 'auto'` list div.
 * While there's more below the fold, the list's last 48px fade out (a mask
 * on the scroll viewport, so it stays pinned to the bottom edge as you
 * scroll) — the cue reads as "more here" without drawing anything over the
 * rows. Clears once scrolled to the end, or if the content never overflows.
 */
const FADE = 'linear-gradient(to bottom, #000 calc(100% - 48px), transparent)';

export const ScrollHintList = ({ style, className, children }: ScrollHintListProps) => {
  const { ref, visible } = useScrollOverflowHint<HTMLDivElement>();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        ...style,
        maskImage: visible ? FADE : undefined,
        WebkitMaskImage: visible ? FADE : undefined,
      }}>
      <div>{children}</div>
    </div>
  );
};
